import { useEffect, useMemo, useState } from "react";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import {
  buildDocFillFields,
  getUnresolvedRequiredFillFields,
  type DocFillField,
} from "@/lib/document-builder/variables/variableFillFields";
import {
  buildInitialVariableValues,
  type DocVariableValues,
} from "@/lib/document-builder/variables/variableFill";
import { docVariablePlaceholder } from "@/lib/document-builder/variables/variableKey";
import { DocumentFormulaFillPreview } from "./DocumentFormulaFillPreview";

type Props = {
  open: boolean;
  documentName: string;
  variables: DocVariableSnapshot[];
  blocks: DocBlock[];
  initialValues?: DocVariableValues;
  onClose: () => void;
  onProceed: (values: DocVariableValues) => void;
  /** When false, empty fields are allowed (manual fill on printed PDF). */
  requireFilledFields?: boolean;
  proceedLabel?: string;
};

export function DocumentFillModal(props: Props) {
  const {
    open,
    documentName,
    variables,
    blocks,
    initialValues,
    onClose,
    onProceed,
    requireFilledFields = true,
    proceedLabel = "Continue to export",
  } = props;
  const [values, setValues] = useState<DocVariableValues>({});
  const [error, setError] = useState<string | null>(null);

  const fillFields = useMemo(
    () => buildDocFillFields(variables, blocks),
    [variables, blocks],
  );

  useEffect(() => {
    if (!open) return;
    const defaults = buildInitialVariableValues(fillFields);
    setValues(initialValues ?? defaults);
    setError(null);
  }, [open, fillFields, initialValues]);

  const unresolvedRequired = useMemo(
    () => getUnresolvedRequiredFillFields(fillFields, values),
    [fillFields, values],
  );

  if (!open) return null;

  function updateValue(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  function handleProceed() {
    if (requireFilledFields && unresolvedRequired.length > 0) {
      setError(
        `Fill required fields: ${unresolvedRequired.map((field) => field.label).join(", ")}`,
      );
      return;
    }
    onProceed(values);
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-fill-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-zinc-900"
      >
        <h3 id="doc-fill-title" className="text-lg font-semibold text-gray-900 dark:text-zinc-100">
          Fill fields
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
          Enter values for <strong>{documentName}</strong> before export. Leave fields blank to
          print a manually fillable PDF with empty lines.
        </p>

        {fillFields.length === 0 ? (
          <p className="mt-4 text-sm text-gray-600 dark:text-zinc-300">
            This document has no fill fields. You can proceed directly to export preview.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {fillFields.map((field) => (
              <FillFieldRow key={field.key} field={field} value={values[field.key] ?? ""} onChange={updateValue} />
            ))}
          </ul>
        )}

        <DocumentFormulaFillPreview blocks={blocks} variableValues={values} />

        {error ? <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p> : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm dark:border-zinc-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleProceed}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            {proceedLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function FillFieldRow(props: {
  field: DocFillField;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const { field, value, onChange } = props;
  const inputClassName =
    "mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800";

  return (
    <li>
      <label className="block text-sm">
        <span className="font-medium text-gray-800 dark:text-zinc-200">
          {field.label}
          {field.required ? (
            <span className="ml-1 text-red-600 dark:text-red-400">*</span>
          ) : null}
        </span>
        <span className="ml-2 font-mono text-[10px] text-gray-500 dark:text-zinc-500">
          {docVariablePlaceholder(field.key)}
        </span>
        {field.inputKind === "multiline" ? (
          <textarea
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            rows={4}
            className={inputClassName}
          />
        ) : field.inputKind === "checkbox" ? (
          <select
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className={inputClassName}
          >
            <option value="">No</option>
            <option value="yes">Yes</option>
          </select>
        ) : (
          <input
            type={field.inputKind === "date" ? "date" : "text"}
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className={inputClassName}
          />
        )}
      </label>
    </li>
  );
}
